// Which airspace a given filter state may DRAW, and therefore which airspace it may talk about.
//
// This lives in one module because the same question is asked from four places that must not be
// allowed to answer it differently: the layer-visibility effect, the merged-footprint filter, the
// effective-mode tooltip, and the legend. When they disagreed, the map showed one thing and its own
// explanation another — and in the case that mattered most, the panel said temporary restrictions
// were being withheld as unreliable while the default view went on drawing them.
//
// Everything here is pure so it can be asserted directly (tests/conformance/visibility.test.ts);
// the safety rule below is not something to verify by looking at a map.

import type { Zone, Notam } from './engine.ts';

/** Every filter the map surfaces, as one value. `notamsOk` is not a filter — see `notamsShown`. */
export interface FilterState {
  layers: { nofly: boolean; allowed: boolean; notam: boolean };
  /**
   * Whether the NOTAM layer arrived at all. Records too old to trust never reach this module
   * (`drawableNotams`). Distinct from `layers.notam`, which is only what the visitor asked for.
   */
  notamsOk: boolean;
  /** Keyed by `String(zone.authority)`. A missing key means visible — new facets default to on. */
  authFacet: Record<string, boolean>;
  catFacet: { nofly: Record<string, boolean>; ok: Record<string, boolean> };
  /** Keyed by NOTAM `area_type`, with `'?'` for a missing one. */
  notamFacet: Record<string, boolean>;
}

/**
 * Whether the NOTAM layer may be drawn AT ALL.
 *
 * Whether it arrived and the visitor's toggle are two different questions and both have to say yes.
 * Withdrawing the checkbox does not withdraw the pixels; only consulting this does.
 */
export const notamsShown = (f: FilterState): boolean => f.layers.notam && f.notamsOk;

/** The facet key for a NOTAM, including the fallback used when the source omits the type. */
export const notamKey = (areaType: string | null | undefined): string => areaType || '?';

/** The facet key for a zone's authority, including the fallback for an unknown one. */
export const authorityKey = (authority: number | null | undefined): string => String(authority ?? -1);

/** Whether a zone of this class/category/authority is currently drawn. */
export function zoneVisible(f: FilterState, cls: 'nofly' | 'ok', category: string, authority: number | null | undefined): boolean {
  if (f.authFacet[authorityKey(authority)] === false) return false;
  return cls === 'nofly'
    ? f.layers.nofly && f.catFacet.nofly[category] !== false
    : f.layers.allowed && f.catFacet.ok[category] !== false;
}

/** Whether a NOTAM of this type is currently drawn. Fails closed on a layer that did not arrive. */
export const notamVisible = (f: FilterState, areaType: string | null | undefined): boolean =>
  notamsShown(f) && f.notamFacet[notamKey(areaType)] !== false;

/** `zoneVisible` for a raw zone record. `category` comes from the caller's own classifier. */
export const zoneRecordVisible = (f: FilterState, z: Zone, category: string): boolean =>
  zoneVisible(f, z.area_type === 909 ? 'nofly' : 'ok', category, z.authority);

/** `notamVisible` for a raw NOTAM record. */
export const notamRecordVisible = (f: FilterState, n: Notam): boolean => notamVisible(f, n.area_type);
